import React, { useEffect, useState } from "react";
import Header from "../WelcomeHeader";
import { ReactComponent as TimeIcon } from "../../../Assets/Icons/timeIcon.svg";
import { ReactComponent as DollarIcon } from "../../../Assets/Icons/dollarIcon.svg";
import { ReactComponent as ListIcon } from "../../../Assets/Icons/listIcon.svg";
import { ReactComponent as AccountSetupIcon } from "../../../Assets/Icons/accountsetup.svg";
import PrimaryButton from "../../common/PrimaryButton";
import { useDispatch, useSelector } from "react-redux";
import { createConnectedAccount, getOnboardingStatus } from "../../../services/fundraiserService";
import { useNavigate } from "react-router-dom";
import { clearAuth } from "../../../redux/slices/authSlice";
import Loader from "../Loader";
import axios from "../../../utils/axios";

const FundraiserWelcome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state?.authReducer?.tokenData?.token);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!token) {
        setIsChecking(false);
        navigate("/fundraiser/account-login", { replace: true });
        return;
      }

      try {
        const statusPromise = await dispatch(getOnboardingStatus()).unwrap();
        const detailsSubmitted = statusPromise?.data?.detailsSubmitted === true;
        const chargesEnabled = statusPromise?.data?.chargesEnabled === true;

        if (detailsSubmitted && chargesEnabled) {
          // Set the flag to indicate Stripe completion
          sessionStorage.setItem('fundraiser_stripe_complete', 'true');

          // Stripe onboarding is complete - check approval status before redirecting
          // Use explicit token like in Login page to ensure it works correctly
          try {
            const checkResponse = await axios.get("fundraiser/profile", {
              headers: {
                Authorization: `Bearer ${token}`,
                "api-key": process.env.REACT_APP_API_KEY
              }
            });

            const fundraiserProfile = checkResponse?.data?.data;
            const profileStatus = typeof fundraiserProfile?.status === "string"
              ? fundraiserProfile.status.toLowerCase()
              : fundraiserProfile?.status;
            const isApproved = profileStatus === "approved";

            if (isApproved) {
              // Approved AND Stripe complete - go to dashboard
              navigate("/fundraiser/dashboard", { replace: true });
              return;
            } else {
              // Stripe complete but NOT approved - clear auth and redirect (no logout API call needed)
              dispatch(clearAuth());
              // Use sessionStorage to persist modal flag across page reloads (Stripe redirect causes full page reload)
              sessionStorage.setItem('showAccountPendingModal', 'true');
              navigate("/fundraiser/account-login", {
                replace: true,
                state: {
                  message: "Your account setup is complete! Your account is pending admin approval. You will be notified once approved.",
                  showAccountPendingModal: true // Flag to show modal for newly created fundraiser
                }
              });
              return;
            }
          } catch (profileError) {
            console.error("Failed to fetch fundraiser profile:", profileError);
            // If profile fetch fails, clear auth and redirect (no logout API call needed)
            dispatch(clearAuth());
            navigate("/fundraiser/account-login", {
              replace: true,
              state: {
                message: "Failed to verify account status. Please try again."
              }
            });
            return;
          }
        } else if (detailsSubmitted && !chargesEnabled) {
          // Stripe details submitted but not charges enabled, go to complete-setup
          navigate("/complete-setup", { replace: true });
          return;
        }
        // If not complete, stay on welcome page
        setIsChecking(false);
      } catch (error) {
        // If error checking status, stay on welcome page
        setIsChecking(false);
      }
    };
    checkOnboardingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = async () => {
    const linkPromise = await dispatch(createConnectedAccount()).unwrap();
    window.location.href = linkPromise?.data;
  };

  const welcomeList = [
    {
      heading: "Account Setup",
      text: "You're almost ready! Connect your Stripe account to receive payments. Click \"Get Started\" to begin.",
      icon: <AccountSetupIcon />,
    },
    {
      heading: "How long does it take?",
      text: "Takes 10 minutes to set up your Fundraiser Dashboard and campaign.",
      icon: <TimeIcon />,
    },
    {
      heading: "What does it cost?",
      text: "Absolutely nothing - it's free to join and free to use, always.",
      icon: <DollarIcon />,
    },
    {
      heading: "Do I need anything before I get started?",
      text: "Have the following information ready: it will help you complete the setup smoothly:",
      details: [
        { title: "Personal Details", description: "Phone number, DOB, Address, Website" },
        { title: "Account Details", description: "Account number, BSB" },
        { title: "Photo ID", description: "Snap a photo on your phone or upload it from your desktop" },
        { title: "Your Industry Type", description: "Education (schools/education services) or Membership Organisations (charities/social services)." }
      ],
      icon: <ListIcon />,
    },
  ];

  // Show loader while checking to prevent dashboard flash
  if (isChecking) {
    return <Loader />;
  }

  return (
    <div className="w-full min-h-screen flex flex-col bg-white">
      <Header />
      <div className="flex-1 flex items-center justify-center px-6 lg:px-12 py-16 lg:py-24">
        <div className="w-full max-w-[1400px] grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          {/* Left Side - Heading */}
          <div className="flex flex-col justify-center lg:pt-24">
            <h1 className="font-poppins font-[500] text-[40px] md:text-[48px] lg:text-[56px] text-[#303030] leading-tight">
              Welcome to{" "}
              <span className="text-[#00ADEF]">Swoodle</span>{" "}
              Fundraiser!
            </h1>
            <p className="font-poppins font-[400] text-[18px] md:text-[20px] lg:text-[22px] text-[#767676] mt-6 leading-relaxed">
              Select Get Started
            </p>
          </div>

          {/* Right Side - Information Items with Dividers */}
          <div className="flex flex-col">
            {welcomeList.map((item, index) => (
              <React.Fragment key={index}>
                <div className="flex items-start justify-between gap-6 py-6">
                  <div className="flex-1">
                    <h3 className="font-poppins font-[600] text-[16px] md:text-[18px] text-[#303030] mb-2">
                      {item.heading}
                    </h3>
                    <p className="font-poppins font-[400] text-[13px] md:text-[14px] text-[#767676] leading-relaxed">
                      {item.text}
                    </p>
                    {item.details && (
                      <ul className="mt-3 space-y-1.5">
                        {item.details.map((detail, idx) => (
                          <li key={idx} className="font-poppins font-[400] text-[12px] md:text-[13px] leading-relaxed pl-5">
                            <span className="text-[#303030] font-[600] -ml-3">• {detail.title}</span>
                            <span className="text-[#767676]"> - {detail.description}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center">
                      {item.icon}
                    </div>
                  </div>
                </div>
                {/* Divider line between items */}
                {index < welcomeList.length - 1 && (
                  <div className="w-full h-[1px] bg-[#E6E6E6]"></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Divider line above button - full width edge to edge */}
      <div className="w-full h-[1px] bg-[#E6E6E6]"></div>

      {/* Bottom Button */}
      <div className="w-full flex justify-end px-6 lg:px-12 py-6 lg:py-8">
        <PrimaryButton
          className="w-[180px] md:w-[200px] font-poppins text-[14px] md:text-[16px] text-center font-[500] h-[45px] md:h-[50px] rounded-[5px]"
          label="Get Started"
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};

export default FundraiserWelcome;